import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  CartErrorEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const CART_SECTION_RENDERED_EVENT = 'cart:section-rendered';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays cart items and keeps every cart surface in sync.
 * Shopify is the source of truth: local DOM is only changed after a successful
 * Ajax Cart API response and a confirmed section render.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #updateInProgress = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.#debouncedOnChange.cancel?.();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line || this.#updateInProgress) return;

    if (quantity === 0) {
      void this.onLineItemRemove(line);
      return;
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow?.querySelector('text-component'));
    textComponent?.shimmer();

    void this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Removes a line item only after Shopify confirms the change.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    if (this.#updateInProgress) return;

    await this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates a cart line through Shopify Ajax Cart API.
   * All cart sections are requested together so drawer/page/icon stay coherent.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    if (this.#updateInProgress) return;

    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    this.#updateInProgress = true;
    this.#disableCartItems();
    cartTotal?.shimmer();

    try {
      const sectionsToUpdate = this.#getCartSectionIds();
      const body = JSON.stringify({
        line,
        quantity,
        sections: sectionsToUpdate.join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const responseText = await response.text();

      let cartResponse;
      try {
        cartResponse = JSON.parse(responseText);
      } catch (error) {
        throw new Error(`Nieprawidłowa odpowiedź koszyka (${response.status}).`);
      }

      resetShimmer(this);

      if (!response.ok || cartResponse.errors) {
        this.#handleCartError(line, cartResponse);
        throw new Error(this.#getCartErrorMessage(cartResponse, response.status));
      }

      this.#updateQuantitySelectors(cartResponse);
      await this.#renderSectionFromCartResponse(cartResponse.sections);
      this.#updateCartQuantitySelectorButtonStates();

      const itemCount = Number.isFinite(Number(cartResponse.item_count))
        ? Number(cartResponse.item_count)
        : Array.isArray(cartResponse.items)
          ? cartResponse.items.reduce((total, item) => total + Number(item.quantity || 0), 0)
          : 0;

      // Dispatch only after the initiating section reflects Shopify's confirmed state.
      // Re-acquire the component because section morphing can replace custom elements.
      const currentComponent = this.#getCurrentComponent();
      currentComponent.dispatchEvent(
        new CartUpdateEvent(cartResponse, this.sectionId, {
          itemCount,
          source: 'cart-items-component',
          sections: cartResponse.sections || {},
        })
      );
    } catch (error) {
      resetShimmer(this);
      console.error('[Valleans cart] Cart update failed', error);

      this.dispatchEvent(
        new CartErrorEvent(
          this.sectionId,
          error instanceof Error ? error.message : 'Nie udało się zaktualizować koszyka.',
          null,
          null
        )
      );
    } finally {
      this.#updateInProgress = false;
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    void this.#handleCartUpdate(event);
  };

  /**
   * Handles a server-side cart error without throwing secondary DOM errors.
   * @param {number} line - The line.
   * @param {Object} cartResponse - Shopify response.
   */
  #handleCartError = (line, cartResponse) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent = this.#getCartErrorMessage(cartResponse);
    }
    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  /**
   * Handles cart updates from product forms, bundles and other cart surfaces.
   * Falls back to Shopify Section Rendering API when bundled section HTML is absent.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent | CustomEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      try {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
        this.#updateCartQuantitySelectorButtonStates();
      } catch (error) {
        console.error('[Valleans cart] Discount section refresh failed', error);
      }
      return;
    }

    if (event.target === this) return;

    try {
      const sections = event.detail?.data?.sections;
      await this.#renderSectionFromCartResponse(sections);

      const cartResource = event.detail?.resource;
      if (cartResource?.items) this.#updateQuantitySelectors(cartResource);
      this.#updateCartQuantitySelectorButtonStates();

      const eventData = event.detail?.data || {};
      document.dispatchEvent(
        new CustomEvent(CART_SECTION_RENDERED_EVENT, {
          detail: {
            sectionId: this.sectionId,
            source: String(eventData.source || ''),
            action: String(eventData.action || ''),
            didError: Boolean(eventData.didError),
          },
        })
      );
    } catch (error) {
      console.error('[Valleans cart] External cart refresh failed', error);
    }
  };

  /**
   * Renders this cart section from bundled HTML or by fetching a fresh section.
   * @param {Record<string, string> | undefined | null} sections
   */
  async #renderSectionFromCartResponse(sections) {
    const sectionHtml = sections?.[this.sectionId];

    if (typeof sectionHtml === 'string' && sectionHtml.trim()) {
      try {
        await morphSection(this.sectionId, sectionHtml);
        return;
      } catch (error) {
        console.warn('[Valleans cart] Bundled section morph failed; fetching fresh section.', error);
      }
    }

    await sectionRenderer.renderSection(this.sectionId, { cache: false });
  }

  /**
   * Returns all active cart section IDs once, without duplicates.
   * @returns {string[]}
   */
  #getCartSectionIds() {
    const sections = new Set([this.sectionId]);
    for (const item of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sections.add(item.dataset.sectionId);
    }
    return Array.from(sections);
  }

  /**
   * Finds the post-morph instance for this section.
   * @returns {CartItemsComponent}
   */
  #getCurrentComponent() {
    for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (component instanceof CartItemsComponent && component.dataset.sectionId === this.sectionId) return component;
    }
    return this;
  }

  /**
   * Normalizes a Shopify error payload to a readable message.
   * @param {Object} response
   * @param {number} [status]
   * @returns {string}
   */
  #getCartErrorMessage(response, status) {
    if (typeof response?.errors === 'string') return response.errors;
    if (response?.errors && typeof response.errors === 'object') {
      const first = Object.values(response.errors).flat().find(Boolean);
      if (first) return String(first);
    }
    if (response?.description) return String(response.description);
    if (response?.message) return String(response.message);
    return status ? `Nie udało się zaktualizować koszyka (${status}).` : 'Nie udało się zaktualizować koszyka.';
  }

  /** Disables cart interactions during a confirmed mutation. */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
  }

  /** Enables cart interactions after mutation settles. */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
  }

  /**
   * Synchronizes data-cart-quantity for every visible quantity selector.
   * Quantities are summed by variant because the same variant can exist on
   * multiple cart lines with different line-item properties.
   * @param {Object} updatedCart
   */
  #updateQuantitySelectors(updatedCart) {
    if (!Array.isArray(updatedCart?.items)) return;

    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = String(item.variant_id || '');
      if (!variantId) continue;
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + Number(item.quantity || 0));
    }

    for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
      if (!(selector instanceof HTMLElement)) continue;
      const variantId = selector.dataset.variantId || '';
      const input = selector.querySelector('input[data-cart-quantity]');
      if (!(input instanceof HTMLInputElement)) continue;

      input.setAttribute('data-cart-quantity', String(quantitiesByVariant.get(variantId) || 0));
      if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
        selector.updateCartQuantity();
      }
    }
  }

  /** Updates button states for all cart quantity selectors after a render. */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string}
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
